import { Injectable } from '@angular/core';
import { DeliveryNoteBundleOverview } from '../../../api/models/delivery-note-bundle-overview';
import { DeliveryNoteBundlesOverview } from '../../../api/models/delivery-note-bundles-overview';
import { lastValueFrom, ReplaySubject, Subject, takeUntil, EMPTY } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { DeliveryNoteBundlesService } from '../../../api/services/delivery-note-bundles.service';
import { StorageService } from '@app/core/services/client-services/storage-service/storage.service';
import { DeliveryNoteInPool } from '../../../api/models/delivery-note-in-pool';
import { DeliveryNotesService } from '../../../api/services/delivery-notes.service';
import { LoginOrganizationSite } from '../../../api/models/login-organization-site';
import { UserRoles } from '../../../api/models/user-roles';
import { getSelectedLocationKeys } from '@app/stores/appStore';

export interface DeliveryNoteBundleWithOrgSiteKey
    extends DeliveryNoteBundleOverview {
    orgSiteKey: string;
    read_only: boolean;
}

export interface DeliveryNoteWithOrgSiteKey extends DeliveryNoteInPool {
    orgSiteKey: string;
    read_only: boolean;
}

@Injectable({
    providedIn: 'root',
})
export class ConsignorService {
    private _allSites: LoginOrganizationSite[] = [];
    private _selectedOrgSites: LoginOrganizationSite[] = [];
    private _currentOrgSiteKey: string = '';

    /**
     * Bundles
     */
    public deliveryNoteBundlesReplaySubject: ReplaySubject<
        DeliveryNoteBundleWithOrgSiteKey[]
    > = new ReplaySubject<DeliveryNoteBundleWithOrgSiteKey[]>(1);
    private deliveryNoteBundles: DeliveryNoteBundleWithOrgSiteKey[] = [];

    /**
     * Delivery notes
     */
    public deliveryNotesReplaySubject: ReplaySubject<
        DeliveryNoteWithOrgSiteKey[]
    > = new ReplaySubject<DeliveryNoteWithOrgSiteKey[]>(1);
    private deliveryNotes: DeliveryNoteWithOrgSiteKey[] = [];

    private destroy$ = new Subject<void>();

    constructor(
        private deliveryNoteBundleService: DeliveryNoteBundlesService,
        private deliveryNotesService: DeliveryNotesService,
        private storageService: StorageService
    ) {}

    /**
     * load bundles
     */
    async loadDeliveryNoteBundleOverviewsForSites(
        orgSiteKey: string
    ): Promise<void> {
        console.log(
            'ConsignorService: loadDeliveryNoteBundleOverviewsForSites called with orgSiteKey:',
            orgSiteKey
        );
        const { setFoldersLoading, setFoldersError, setFolders } = await import(
            '@app/stores/foldersStore'
        );

        setFoldersLoading(true);
        setFoldersError(null);

        const allFolders: import('@app/stores/foldersStore').FolderData[] = [];

        try {
            // Find the specific site to get role information
            const site = this._selectedOrgSites.find(
                s => s._key === orgSiteKey
            );

            const bundleOverviews: DeliveryNoteBundlesOverview =
                await lastValueFrom(
                    this.deliveryNoteBundleService
                        .getBundles({
                            organizationSiteKey: orgSiteKey,
                        })
                        .pipe(
                            takeUntil(this.destroy$),
                            catchError(() => {
                                // Silently handle cancellation
                                return EMPTY;
                            })
                        ),
                    { defaultValue: [] }
                );

            bundleOverviews.forEach(overview => {
                const folderData = {
                    orgSiteKey: orgSiteKey,
                    read_only: site
                        ? site.roles.includes(UserRoles.RemoteConsignor) &&
                          !site.roles.includes(UserRoles.Consignor)
                        : false,
                    ...overview,
                };
                allFolders.push(folderData as any);
            });

            // Update the store with the specific site's data
            setFolders(allFolders);
            setFoldersLoading(false);
        } catch (error: any) {
            // Ignore errors from cancelled requests
            if (error?.name !== 'EmptyError') {
                // Extract request ID from error if available
                const requestId =
                    error?.headers?.get?.('x-request-id') ||
                    error?.error?.requestId ||
                    null;
                const errorMessage = requestId
                    ? `GENERAL.ERRORS.FAILED_TO_LOAD_FOLDERS|${requestId}`
                    : 'GENERAL.ERRORS.FAILED_TO_LOAD_FOLDERS';
                setFoldersError(errorMessage);
                setFoldersLoading(false);
                throw error;
            }
        }
    }
    /**
     * load delivery notes
     */
    async loadDeliveryNotesForSites(): Promise<void> {
        this.deliveryNotes = [];
        for (const site of this._selectedOrgSites) {
            try {
                const notes = await lastValueFrom(
                    this.deliveryNotesService
                        .getDeliveryNotesMetadataInPool({
                            organizationSiteKey: site._key,
                        })
                        .pipe(
                            takeUntil(this.destroy$),
                            catchError(() => {
                                // Silently handle cancellation
                                return EMPTY;
                            })
                        ),
                    { defaultValue: [] }
                );

                notes.forEach(note => {
                    this.deliveryNotes.push({
                        orgSiteKey: site._key,
                        read_only:
                            site.roles.includes(UserRoles.RemoteConsignor) &&
                            !site.roles.includes(UserRoles.Consignor),
                        ...note,
                    });
                });
            } catch (error: any) {
                // Ignore errors from cancelled requests
                if (error?.name !== 'EmptyError') {
                    throw error;
                }
            }
        }
        this.deliveryNotesReplaySubject.next(this.deliveryNotes);
    }

    saveSitesSelection(sites: LoginOrganizationSite[]) {
        this._selectedOrgSites = sites;
        this.storageService.setItem(
            'consignorSiteSelection',
            sites.map(site => site._key)
        );
    }

    loadSitesSelection(): LoginOrganizationSite[] {
        const sites: string[] = this.storageService.getItemAsObject(
            'consignorSiteSelection'
        );
        if (this._allSites && sites) {
            this._selectedOrgSites = [];
            for (const siteId of sites) {
                const site = this._allSites.find(
                    orgSite => orgSite._key === siteId
                );
                if (site) {
                    this._selectedOrgSites.push(site);
                }
            }
        }
        return this._selectedOrgSites;
    }

    saveReferencedSiteKey(siteKey: string) {
        this.storageService.setItem('referencedSiteKeyConsignor', siteKey);
    }

    loadReferencedSiteKey(): void {
        this._currentOrgSiteKey = this.storageService.getItemAsObject(
            'referencedSiteKeyConsignor'
        );
    }

    isContentVisible(siteId: string): boolean {
        if (siteId) {
            // Use the appStore to check if the site is in the selected locations for consignor role
            const selectedLocationKeys = getSelectedLocationKeys('consignor');
            return selectedLocationKeys.includes(siteId);
        }
        return false;
    }

    /**
     * getter setter
     */
    get selectedOrgSites(): string[] {
        return this._selectedOrgSites.map(site => site._key);
    }

    set allSites(value: LoginOrganizationSite[]) {
        this._allSites = value;
    }

    get allSites(): LoginOrganizationSite[] {
        return this._allSites;
    }

    get currentOrgSiteKey(): string {
        return this._currentOrgSiteKey;
    }

    set currentOrgSiteKey(value: string) {
        this._currentOrgSiteKey = value;
        this.saveReferencedSiteKey(value);
    }

    /**
     * Cancel all active HTTP requests
     */
    cancelActiveRequests(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.destroy$ = new Subject<void>();
    }
}
